/**
 * User Service
 * API integration for user CRUD operations and bindings management
 */

import apiClient from './apiClient';
import {
  User,
  UserBinding,
  UserRole,
  UserListParams,
  CreateUserRequest,
  CreateUserResponse,
  UpdateUserRequest,
  AddBindingRequest,
  UserStatus,
  removeCPFMask,
  removeWhatsAppMask,
} from '../types/user';
import { PaginatedResponse } from '../types/common';

// ==================== RESPONSE TYPES ====================

interface UserListResponse {
  data: User[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ==================== USER SERVICE ====================

class UserService {
  private baseUrl = '/users';

  /**
   * Get paginated list of users
   */
  async getUsers(params: UserListParams = {}): Promise<PaginatedResponse<User>> {
    const { page = 1, limit = 20, search, filters, sortBy, sortOrder } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) {
      queryParams.append('search', search);
    }

    if (sortBy) {
      queryParams.append('sortBy', sortBy);
    }

    if (sortOrder) {
      queryParams.append('sortOrder', sortOrder);
    }

    if (filters) {
      // Status filter - support single or multiple
      if (filters.situacao) {
        if (Array.isArray(filters.situacao)) {
          queryParams.append('situacao', filters.situacao.join(','));
        } else {
          queryParams.append('situacao', filters.situacao);
        }
      }

      // Company filter - support single or multiple
      if (filters.companyId) {
        if (Array.isArray(filters.companyId)) {
          queryParams.append('companyId', filters.companyId.join(','));
        } else {
          queryParams.append('companyId', filters.companyId.toString());
        }
      }

      // Provider filter - support single or multiple
      if (filters.providerId) {
        if (Array.isArray(filters.providerId)) {
          queryParams.append('providerId', filters.providerId.join(','));
        } else {
          queryParams.append('providerId', filters.providerId.toString());
        }
      }
    }

    const response = await apiClient.get<UserListResponse>(`${this.baseUrl}?${queryParams}`);

    // Transform backend response to PaginatedResponse
    const data = response.data;
    return {
      data: data?.data ?? [],
      pagination: {
        total: data?.total || 0,
        page: data?.page || page,
        limit: data?.limit || limit,
        totalPages: data?.totalPages || Math.ceil((data?.total || 0) / limit),
      },
    };
  }

  /**
   * Get user by ID
   */
  async getUserById(id: number): Promise<User> {
    console.log('Fetching user:', id);

    const response = await apiClient.get<User>(`${this.baseUrl}/${id}`);

    console.log('User fetched:', response.data);
    return response.data as User;
  }

  /**
   * Create new user
   */
  async createUser(data: CreateUserRequest): Promise<CreateUserResponse> {
    console.log('Creating user:', { ...data, password: data.password ? '***' : undefined });

    // Clean up CPF and WhatsApp before sending
    const payload = {
      ...data,
      cpf: removeCPFMask(data.cpf),
      whatsapp: data.whatsapp ? removeWhatsAppMask(data.whatsapp) : undefined,
    };

    const response = await apiClient.post<CreateUserResponse>(`${this.baseUrl}`, payload);

    console.log('User created successfully:', response.data);
    return response.data as CreateUserResponse;
  }

  /**
   * Update existing user
   */
  async updateUser(id: number, data: UpdateUserRequest): Promise<User> {
    console.log('Updating user:', { id, data });

    // Clean up WhatsApp if provided
    const payload = {
      ...data,
      whatsapp: data.whatsapp ? removeWhatsAppMask(data.whatsapp) : undefined,
    };

    const response = await apiClient.put<User>(`${this.baseUrl}/${id}`, payload);

    console.log('User updated successfully:', response.data);
    return response.data as User;
  }

  /**
   * Delete user (soft delete - sets status to Inativo)
   */
  async deleteUser(id: number): Promise<void> {
    console.log('Deleting user:', id);

    await apiClient.delete(`${this.baseUrl}/${id}`);

    console.log('User deleted successfully');
  }

  /**
   * Toggle user status (active/inactive)
   */
  async toggleUserStatus(id: number, currentStatus: UserStatus): Promise<User> {
    console.log('Toggling user status:', { id, currentStatus });

    const newStatus = currentStatus === UserStatus.ATIVO ? UserStatus.INATIVO : UserStatus.ATIVO;

    return this.updateUser(id, { situacao: newStatus });
  }

  // ==================== BINDINGS METHODS ====================

  /**
   * Get user bindings
   */
  async getUserBindings(userId: number): Promise<UserBinding[]> {
    console.log('Fetching user bindings:', userId);

    const response = await apiClient.get<UserBinding[]>(`${this.baseUrl}/${userId}/bindings`);

    console.log('User bindings fetched:', response.data);
    return response.data as UserBinding[] ?? [];
  }

  /**
   * Add binding to user
   */
  async addUserBinding(userId: number, data: AddBindingRequest): Promise<UserBinding> {
    console.log('Adding binding to user:', { userId, data });

    const response = await apiClient.post<UserBinding>(`${this.baseUrl}/${userId}/bindings`, data);

    console.log('Binding added successfully:', response.data);
    return response.data as UserBinding;
  }

  /**
   * Remove binding from user
   */
  async removeUserBinding(userId: number, bindingId: number): Promise<void> {
    console.log('Removing binding from user:', { userId, bindingId });

    await apiClient.delete(`${this.baseUrl}/${userId}/bindings/${bindingId}`);

    console.log('Binding removed successfully');
  }

  // ==================== SUPPORTE ====================

  /** Designa este usuário como responsável pelo suporte — desmarca quem estava antes. */
  async setSupportResponsible(userId: number): Promise<User> {
    const response = await apiClient.post<User>(`${this.baseUrl}/${userId}/support-responsible`, {});
    return response.data as User;
  }

  /** Tira o usuário da responsabilidade pelo suporte, sem escolher outra pessoa no lugar. */
  async removeSupportResponsible(userId: number): Promise<User> {
    const response = await apiClient.delete<User>(`${this.baseUrl}/${userId}/support-responsible`);
    return response.data as User;
  }

  // ==================== VALIDATION HELPERS ====================

  /**
   * Check if email is available (not registered)
   * Note: Backend validates on create, this is for real-time validation
   */
  async checkEmailAvailable(email: string, excludeUserId?: number): Promise<boolean> {
    try {
      const response = await this.getUsers({
        search: email,
        limit: 1,
      });

      // If no users found with this email, it's available
      if (response.data.length === 0) {
        return true;
      }

      // If found but it's the same user (editing), it's available
      if (excludeUserId && response.data[0].id === excludeUserId) {
        return true;
      }

      // Check if email matches exactly
      return response.data[0].email.toLowerCase() !== email.toLowerCase();
    } catch (error) {
      console.error('Error checking email availability:', error);
      // Return true on error to avoid blocking form submission
      return true;
    }
  }

  /**
   * Check if CPF is available (not registered)
   * Note: Backend validates on create, this is for real-time validation
   */
  async checkCpfAvailable(cpf: string, excludeUserId?: number): Promise<boolean> {
    try {
      const cleanCpf = removeCPFMask(cpf);
      const response = await this.getUsers({
        search: cleanCpf,
        limit: 1,
      });

      // If no users found with this CPF, it's available
      if (response.data.length === 0) {
        return true;
      }

      // If found but it's the same user (editing), it's available
      if (excludeUserId && response.data[0].id === excludeUserId) {
        return true;
      }

      // Check if CPF matches exactly
      return removeCPFMask(response.data[0].cpf) !== cleanCpf;
    } catch (error) {
      console.error('Error checking CPF availability:', error);
      // Return true on error to avoid blocking form submission
      return true;
    }
  }

  // ==================== STATS METHODS ====================

  /**
   * Get user statistics (by status)
   */
  async getUserStats(): Promise<{
    total: number;
    byStatus: Record<UserStatus, number>;
  }> {
    // Fetch all users to calculate stats (can be optimized with backend endpoint)
    const response = await this.getUsers({ limit: 1000 });

    const stats = {
      total: response.pagination.total,
      byStatus: {} as Record<UserStatus, number>,
    };

    // Initialize counters
    Object.values(UserStatus).forEach((status) => {
      stats.byStatus[status] = 0;
    });

    // Count users
    response.data.forEach((user) => {
      if (stats.byStatus[user.situacao] !== undefined) {
        stats.byStatus[user.situacao]++;
      }
    });

    return stats;
  }

  /**
   * Papéis (perfis de acesso) do usuário.
   */
  async getUserRoles(userId: number): Promise<UserRole[]> {
    const response = await apiClient.get<UserRole[]>(`${this.baseUrl}/${userId}/roles`);

    return response.data ?? [];
  }

  /**
   * Concede um papel. O backend recusa papel mais poderoso que o de quem concede — é por isso
   * que a tela mostra a mensagem dele, em vez de esconder as opções.
   */
  async assignRole(userId: number, roleId: number): Promise<UserRole[]> {
    const response = await apiClient.post<UserRole[]>(`${this.baseUrl}/${userId}/roles`, {
      roleId,
    });

    return response.data ?? [];
  }

  /** Tira um papel do usuário. */
  async removeRole(userId: number, roleId: number): Promise<UserRole[]> {
    const response = await apiClient.delete<UserRole[]>(
      `${this.baseUrl}/${userId}/roles/${roleId}`
    );

    return response.data ?? [];
  }

  /**
   * Health check for users system
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get<{
      service: string;
      version: string;
      status: string;
      timestamp: string;
    }>(`${this.baseUrl}/health`);

    return {
      status: response.data?.status || 'unknown',
      timestamp: response.data?.timestamp || new Date().toISOString(),
    };
  }
}

// Export singleton instance
export const userService = new UserService();
export default userService;
